import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MembershipSection } from './MembershipSection';
import { ChangeEmailForm } from './ChangeEmailForm';
import { ChangePasswordForm } from './ChangePasswordForm';
import { DeactivateAccountModal } from './DeactivateAccountModal';
import { useDeactivateAccount } from '../../hooks/useAccountManagement';

interface AccountSectionProps {
  checkoutSuccess: boolean;
  expectedTier: string | null;
  expectedInterval: string | null;
  onCheckoutHandled: () => void;
}

/**
 * Detect if user has an email+password identity (not OAuth-only).
 * OAuth-only users (Google sign-in) should not see Change Password/Email.
 * Falls back to app_metadata.providers and app_metadata.provider when
 * identities array is unavailable (e.g. token refresh edge cases).
 */
function hasEmailIdentity(user: {
  identities?: Array<{ provider: string }>;
  app_metadata?: { providers?: string[]; provider?: string };
} | null): boolean {
  // Primary check: identities array
  if (user?.identities?.length) {
    return user.identities.some((id) => id.provider === 'email');
  }
  // Fallback: app_metadata.providers array
  if (user?.app_metadata?.providers?.length) {
    return user.app_metadata.providers.includes('email');
  }
  // Fallback: app_metadata.provider string (single provider)
  if (user?.app_metadata?.provider) {
    return user.app_metadata.provider === 'email';
  }
  return false;
}

export function AccountSection({ checkoutSuccess, expectedTier, expectedInterval, onCheckoutHandled }: AccountSectionProps) {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const deactivateAccount = useDeactivateAccount();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  const showAuthForms = hasEmailIdentity(user);

  const handleEditProfile = () => {
    if (profile?.username) {
      navigate(`/u/${profile.username}?edit=true`);
    }
  };

  return (
    <>
      <div className="space-y-6">
        {/* Membership — frozen, rendered as-is */}
        <MembershipSection
          checkoutSuccess={checkoutSuccess}
          expectedTier={expectedTier}
          expectedInterval={expectedInterval}
          onCheckoutHandled={onCheckoutHandled}
        />

        {/* Account Actions Card */}
        <div className="bg-[#1A1F26] rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">ACCOUNT</h2>

          {/* Edit Profile */}
          <div className="flex items-center justify-between">
            <div>
              <div className="text-white font-medium">Edit Profile</div>
              <div className="text-sm text-slate-400">Update your display name, bio, and avatar</div>
            </div>
            <button
              onClick={handleEditProfile}
              className="px-4 py-2 bg-[#2A2F36] hover:bg-[#3A3F46] text-white rounded-lg transition-colors font-medium"
            >
              Edit
            </button>
          </div>

          {/* Change Email — hidden for OAuth-only users */}
          {showAuthForms && <ChangeEmailForm />}

          {/* Change Password — hidden for OAuth-only users */}
          {showAuthForms && <ChangePasswordForm />}

          {/* Deactivate Account */}
          <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
            <div>
              <div className="text-white font-medium">Deactivate Account</div>
              <div className="text-sm text-slate-400">Deactivate your account (30-day recovery period)</div>
            </div>
            <button
              onClick={() => setShowDeactivateModal(true)}
              disabled={deactivateAccount.isPending}
              className="px-4 py-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-lg transition-colors font-medium disabled:opacity-50"
            >
              Deactivate
            </button>
          </div>

          {/* Sign Out */}
          <div className="pt-4 border-t border-white/[0.06]">
            <div className="flex items-center justify-center">
              <button
                onClick={async () => {
                  await signOut();
                  navigate('/');
                }}
                className="px-6 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors font-medium"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {showDeactivateModal && (
        <DeactivateAccountModal
          isOpen={showDeactivateModal}
          onClose={() => setShowDeactivateModal(false)}
          onConfirm={() => {
            deactivateAccount.mutate();
            setShowDeactivateModal(false);
          }}
          isDeactivating={deactivateAccount.isPending}
        />
      )}
    </>
  );
}
